import type { RenderQuality } from '../../rendering/renderQuality';
import {
  SPEED_LINES_COUNT,
  SPEED_LINES_DRIFT_FRAC_PER_BEAT,
  SPEED_LINES_LENGTH_FRAC,
  SPEED_LINES_MAX_ALPHA,
  SPEED_LINES_MIN_ENERGY,
} from './jumpRenderConstants';

interface SpeedLine {
  /** Vertical position as a fraction of canvas height (kept above the ground band). */
  normY: number;
  /** Per-line length variation (0.5..1). */
  lengthMul: number;
  /** Base horizontal offset (0..1 of the wrap width) so lines aren't aligned. */
  phase: number;
  /** Per-line opacity variation (0.5..1) for depth. */
  alphaMul: number;
}

/**
 * "In the zone" speed lines — faint horizontal streaks that rush leftward
 * (matching the obstacle scroll) once a streak pushes `comboEnergy` past
 * {@link SPEED_LINES_MIN_ENERGY}, so a hot run literally feels faster. A seeded,
 * fixed lane set (no per-frame allocation, mirroring the scene's star field);
 * beat-locked drift keeps it deterministic with the scroll. Additive + low
 * alpha so incoming hazards stay readable. Drawn behind the obstacles. Disabled
 * on the low tier (`decorations`) and skipped under reduced motion.
 */
export class JumpSpeedLines {
  private readonly lines: SpeedLine[] = [];
  private readonly enabled: boolean;

  constructor(quality: RenderQuality) {
    this.enabled = quality.decorations;
    if (this.enabled) {
      this.initLines(SPEED_LINES_COUNT);
    }
  }

  private initLines(count: number): void {
    for (let i = 0; i < count; i += 1) {
      const ySeed = (i * 7919 + 104729) % 233280;
      const lenSeed = (i * 1213 + 53) % 100;
      const phaseSeed = (i * 6151 + 17) % 233280;
      const alphaSeed = (i * 97 + 41) % 100;
      this.lines.push({
        // Keep streaks in the upper playfield so they don't wash over the ground band.
        normY: (ySeed / 233280) * 0.82,
        lengthMul: 0.5 + (lenSeed / 100) * 0.5,
        phase: phaseSeed / 233280,
        alphaMul: 0.5 + (alphaSeed / 100) * 0.5,
      });
    }
  }

  draw(
    ctx: CanvasRenderingContext2D,
    width: number,
    height: number,
    beat: number,
    comboEnergy: number,
    accent: string,
    reducedMotion: boolean,
  ): void {
    if (!this.enabled || reducedMotion || comboEnergy < SPEED_LINES_MIN_ENERGY) {
      return;
    }
    // Fade in across [MIN_ENERGY, 1] so the streaks build with the streak
    // rather than snapping on the instant the threshold is crossed.
    const ramp = Math.min(1, (comboEnergy - SPEED_LINES_MIN_ENERGY) / (1 - SPEED_LINES_MIN_ENERGY));
    const maxLen = width * SPEED_LINES_LENGTH_FRAC * ramp;
    if (maxLen < 1) {
      return;
    }
    const wrap = width * 1.5;
    const drift = (beat * width * SPEED_LINES_DRIFT_FRAC_PER_BEAT) % wrap;
    ctx.save();
    ctx.globalCompositeOperation = 'lighter';
    ctx.strokeStyle = accent;
    ctx.lineCap = 'round';
    ctx.lineWidth = 2;
    for (const line of this.lines) {
      const len = maxLen * line.lengthMul;
      const baseX = line.phase * wrap;
      const x = ((((baseX - drift) % wrap) + wrap) % wrap) - width * 0.25;
      const y = line.normY * height;
      ctx.globalAlpha = SPEED_LINES_MAX_ALPHA * ramp * line.alphaMul;
      ctx.beginPath();
      ctx.moveTo(x, y);
      ctx.lineTo(x + len, y);
      ctx.stroke();
    }
    ctx.restore();
  }
}
